<template>
  <DefaultLayout>
    <div class="text-gray-800 antialiased">
      <main>
        <div
          class="relative pb-40 flex content-center items-center justify-center"
          style="min-height: 75vh; z-index: 1;"
        >
          <div
            class="absolute top-0 w-full h-full bg-center bg-cover"
            :style="{ backgroundImage: 'url(' + heroBgUrl + ')' }"
          >
            <span
              id="blackOverlay"
              class="w-full h-full absolute opacity-75 bg-black"
            ></span>
          </div>
          <div class="container relative mx-auto">
            <div class="items-center flex flex-wrap">
              <div class="w-full lg:w-6/12 px-4 ml-auto mr-auto text-center">
                <div class="lg:pr-12">
                  <h1 class="text-white font-semibold md:text-5xl text-3xl">
                    Growing and Pushing Boundaries
                  </h1>
                  <p class="mt-4 text-lg text-gray-300">
                    Insurance Express and Member Companies are a group of
                    companies who work together to provide comprehensive insurance solutions
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="absolute bottom-0 left-0 w-full overflow-hidden" style="transform: translateY(1px); z-index: 2;">
            <svg class="relative w-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 1440 240">
              <path fill="#F2F2F4" fill-opacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,128C672,128,768,160,864,165.3C960,171,1056,149,1152,133.3C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>
        <section class="relative pb-12 bg-[#F2F2F4] pt-4 z-10">
          <div class="container mx-auto px-2">
            <h2 class="text-3xl font-bold text-center mb-6 pt-10">Our Member Companies</h2>
            <p class="text-gray-600 text-center max-w-4xl mx-auto mb-8">
              Together, our member companies form a comprehensive ecosystem of insurance solutions, technology platforms, and specialized services - all working in harmony to deliver exceptional value to our clients and partners.
            </p>
            
            <!-- Brick pattern layout for member companies -->
            <div class="brick-layout">
              <!-- QuoteSlash - Brick 1 (larger) -->
              <div class="brick brick-lg">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://www.quoteslash.com">
                      <img
                        class="mx-auto"
                        src="https://quoteslash.com/wp/wp-content/uploads/2019/05/logo.png"
                        alt="Quoteslash"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Home Insurance for Mortgage Professionals
                    </h6>
                    <p class="text-gray-600 text-sm">
                      Quote Slash is the only online platform dedicated to
                      providing fast, accurate home insurance quotes to mortgage
                      and real estate professionals. Our proprietary software
                      scans all markets, quoting every insurance carrier
                      within minutes.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Rocket Flood - Brick 2 -->
              <div class="brick">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://rocketflood.com/">
                      <img
                        class="mx-auto"
                        :src="rocketFloodLogo"
                        alt="Rocket Flood"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      #1 Flood Insurance Agency
                    </h6>
                    <p class="text-gray-600 text-sm">
                      We are a super powered insurance agency specializing in
                      personal and commercial flood insurance across 49 states.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Rocket MGA - Brick 3 -->
              <div class="brick">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://rocketmga.com/">
                      <img
                        class="mx-auto"
                        src="https://rocketmga.com/logo.svg"
                        alt="Rocket MGA"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Complete Flood Insurance Solutions
                    </h6>
                    <p class="text-gray-600 text-sm">
                      Rocket MGA provides agencies direct appointments with 10+ private flood carriers offering 20+ unique programs.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Str8line Group - Brick 4 (tall) -->
              <div class="brick brick-tall">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://www.str8linegroup.com">
                      <img
                        class="mx-auto"
                        :src="str8lineLogo"
                        alt="Str8line"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Access the Insurance Express Model
                    </h6>
                    <p class="text-gray-600 text-sm">
                      When a proven insurance alliance in the West met an equally
                      top-selling insurance firm in the East, we formed Str8line Insurance
                      Group, an industry-changing, bold insurance franchise model.
                    </p>
                  </div>
                </div>
              </div>

              <!-- NuBuild - Brick 5 -->
              <div class="brick">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://nubuildinsurance.com/">
                      <img
                        class="mx-auto max-h-[80px] w-auto"
                        src="https://nubuildinsurance.com/wp-content/uploads/2020/07/logo.svg"
                        alt="NuBuild"
                        style="min-height: 60px;"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      New Construction Insurance
                    </h6>
                    <p class="text-gray-600 text-sm">
                      NuBuild Insurance Group is a subsidiary of Insurance
                      Express, specializing in new construction homes.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Protego VIP - Brick 6 (wide) -->
              <div class="brick brick-wide">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://www.protegovip.com">
                      <img
                        class="mx-auto"
                        :src="protegoVipLogo"
                        alt="Protego VIP"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">High Value Insurance</h6>
                    <p class="text-gray-600 text-sm">
                      Customized protection for the distinguished client. Protego
                      VIP offers a white glove experience and access to high value
                      insurance markets, providing bespoke coverage solutions.
                    </p>
                  </div>
                </div>
              </div>

              <!-- TheLeadExchange - Brick 7 -->
              <div class="brick">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://www.theleadexchange.com">
                      <img
                        class="mx-auto"
                        :src="theLeadExchangeLogo"
                        alt="The Lead Exchange"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Access to Vast Markets
                    </h6>
                    <p class="text-gray-600 text-sm">
                      By bridging access to every A-rated homeowner's insurance
                      company in Florida, we assist partner agents to capture
                      and retain more business.
                    </p>
                  </div>
                </div>
              </div>

              <!-- The IE Pipeline - Brick 8 -->
              <div class="brick">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://theiepipeline.com/">
                      <img
                        class="mx-auto"
                        src="https://theiepipeline.com/assets/IEpipelineLogo-c4ca4e22.png"
                        alt="The IE Pipeline"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Lead-to-Policy Conversion
                    </h6>
                    <p class="text-gray-600 text-sm">
                      The IE Pipeline transforms how agencies manage prospects, optimizing the customer acquisition journey.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Str8line Enterprises - Brick 9 (wide) -->
              <div class="brick brick-wide">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://www.str8linegroup.com">
                      <img
                        class="mx-auto"
                        :src="str8lineLogo"
                        alt="Str8line Enterprises"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Empowering Independent Agencies
                    </h6>
                    <p class="text-gray-600 text-sm">
                      Str8line Enterprises provides infrastructure, technology and carrier relationships independent agencies need to thrive in today's competitive market.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Graham Newton & McMahon Inc. - Brick 10 -->
              <div class="brick">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://gnminsurance.com/">
                      <img
                        class="mx-auto bg-blue-800 p-1 rounded"
                        src="https://gnminsurance.com/assets/GNM_Logo_New_White.png"
                        alt="Graham Newton & McMahon Inc."
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Full-Service Insurance
                    </h6>
                    <p class="text-gray-600 text-sm">
                      With decades of expertise, Graham Newton & McMahon delivers personalized insurance solutions to individuals and businesses.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Noble Care - Brick 11 (larger) -->
              <div class="brick brick-lg">
                <div class="brick-content">
                  <div class="brick-inner">
                    <a href="https://www.noblecareinsurance.com/">
                      <img
                        class="mx-auto max-h-[80px] w-auto"
                        src="https://www.noblecareinsurance.com/build/assets/NobleCareHealthLogo-BxQRJtjP.svg"
                        alt="Noble Care"
                        style="min-height: 60px;"
                      />
                    </a>
                    <h6 class="text-lg font-semibold">
                      Specialized Health Solutions
                    </h6>
                    <p class="text-gray-600 text-sm">
                      Noble Care specializes in navigating the complex healthcare insurance landscape, offering tailored coverage solutions for individuals, families, and businesses.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from '@/layouts/DefaultLayout.vue';

// Import images
import rocketFloodLogo from '@assets/images/brand/logos/rocket-flood.png';
import quoteslashLogo from '@assets/images/brand/logos/quoteslash.svg';
import str8lineLogo from '@assets/images/brand/logos/str8line.png';
import nubuildLogo from '@assets/images/brand/logos/nubuildlogo.svg';
import protegoVipLogo from '@assets/images/brand/logos/protego-vip-1.png';
import theLeadExchangeLogo from '@assets/images/brand/logos/theleadexchangelogo.jpg';

export default {
  name: 'AboutUs',
  components: {
    DefaultLayout,
  },
  data() {
    return {
      // Make imported images available to the template
      rocketFloodLogo,
      quoteslashLogo,
      str8lineLogo,
      nubuildLogo,
      protegoVipLogo,
      theLeadExchangeLogo,
      heroBgUrl: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1267&q=80'
    };
  }
};
</script>

<style scoped>
/* Brick pattern layout styles */
.brick-layout {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-auto-rows: 1fr;
  grid-gap: 15px;
  position: relative;
  margin-bottom: 2rem;
}

/* Brick styling to create "masonry" effect */
.brick {
  position: relative;
  break-inside: avoid;
  display: flex;
}

.brick-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
  border-left: 4px solid #f97315; /* Brick-like left border */
}

.brick-inner {
  padding: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.brick-inner img {
  max-height: 70px;
  min-height: 40px;
  width: auto;
  object-fit: contain;
  margin-bottom: 10px;
  display: block;
}

.brick-inner h6 {
  margin-top: 0;
  margin-bottom: 8px;
}

.brick-inner p {
  margin-top: 0;
  margin-bottom: 0;
}

/* Different brick sizes for visual interest */
.brick-lg {
  grid-column: span 2;
  grid-row: span 2;
}

.brick-lg .brick-inner img {
  max-height: 85px;
}

.brick-wide {
  grid-column: span 2;
}

.brick-tall {
  grid-row: span 2;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .brick-layout {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    grid-gap: 10px;
  }
  
  .brick-lg, .brick-wide {
    grid-column: span 1;
  }
  
  .brick-lg, .brick-tall {
    grid-row: span 1;
  }
  
  .brick-inner {
    padding: 10px;
  }
  
  .brick-inner h6 {
    font-size: 14px;
  }
  
  .brick-inner p {
    font-size: 12px;
  }
}

/* Additional styles to create brick-like appearance */
.brick:nth-child(odd) .brick-content {
  border-left: 4px solid #e11d48; /* Red brick edge */
}

.brick:nth-child(3n) .brick-content {
  border-left: 4px solid #7c3aed; /* Purple brick edge */
}

.brick:nth-child(4n) .brick-content {
  border-left: 4px solid #0284c7; /* Blue brick edge */
}

.brick:hover .brick-content {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}
</style>
